<template>
    <app-drawer
        ref="timelineDrawer"
        class="trip-timeline-drawer"
        :drawer-id="drawerId"
        drawer-width="40vw"
        :drawer-header-class="'trip-timeline-drawer__drawer-header'"
        :drawer-body-class="'trip-timeline-drawer__drawer-body'"
        :drawer-panel-class="'trip-timeline-drawer__drawer-panel'"
        @close-modal="closeModal"
    >
        <template slot="header">
            <div class="trip-timeline-drawer__header">
                <div class="trip-timeline-drawer__header-copy">
                    <span class="trip-timeline-drawer__eyebrow">{{ $t('tracking_timeline') }}</span>
                    <h5 class="modal-title">{{ timelineTitle }}</h5>

                    <div class="trip-timeline-drawer__meta">
                        <span class="trip-timeline-drawer__meta-pill">
                            {{ `${$t('lot_number')}: ${timelineData.lot_number || '---'}` }}
                        </span>
                        <span class="trip-timeline-drawer__meta-pill">
                            {{ `${$t('horse')}: ${timelineData.horse || '---'}` }}
                        </span>
                        <span class="trip-timeline-drawer__meta-pill trip-timeline-drawer__meta-pill--accent">
                            {{ itemCountLabel }}
                        </span>
                    </div>
                </div>

                <div class="trip-timeline-drawer__header-actions">
                    <button
                        type="button"
                        class="trip-timeline-drawer__refresh"
                        :disabled="preloader"
                        :title="$t('refresh')"
                        @click.prevent="fetchTimeline"
                    >
                        <app-icon name="refresh-cw" class="size-16" />
                    </button>
                    <button type="button" class="close outline-none trip-timeline-drawer__close" @click.prevent="closeDrawer">
                        <span class="mb-1">
                            <app-icon :name="'x'"></app-icon>
                        </span>
                    </button>
                </div>
            </div>
        </template>

        <template slot="body">
            <div class="trip-timeline-drawer__body-shell">
                <app-overlay-loader v-if="preloader" />

                <template v-else-if="items.length">
                    <div class="trip-timeline-drawer__summary">
                        <span class="trip-timeline-drawer__summary-kicker">{{ $t('operational_brief') }}</span>
                        <p class="trip-timeline-drawer__summary-text">
                            {{ $t('tracking_timeline') }}
                        </p>
                    </div>

                    <app-timeline
                        item-type="tracking-timeline-item"
                        :items="items"
                    />
                </template>

                <div v-else class="trip-timeline-drawer__empty">
                    <div class="trip-timeline-drawer__empty-icon">
                        <app-icon :name="'clock'" class="size-18" />
                    </div>
                    <strong>{{ $t('no_timeline_available') }}</strong>
                    <span>{{ $t('nothing_to_show_here') }}</span>
                </div>
            </div>
        </template>
    </app-drawer>
</template>

<script>
import { FormMixin } from '../../../../core/mixins/form/FormMixin';
import { GET_TRACKING } from '../../../Config/ApiUrl';
import { ModalMixin } from '../../../Mixins/ModalMixin';

export default {
    name: 'EventTimelineModal',
    mixins: [FormMixin, ModalMixin],
    props: {
        timelineData: {
            type: Object
        }
    },
    data() {
        return {
            items: [],
        };
    },
    computed: {
        drawerId() {
            return `event-timeline-drawer-${this.timelineData?.trip_id || 'default'}`;
        },
        timelineTitle() {
            const segments = [
                this.timelineData?.lot_number ? `${this.$t('lot_number')} ${this.timelineData.lot_number}` : '',
                this.timelineData?.horse ? `${this.$t('horse')} ${this.timelineData.horse}` : '',
            ].filter(Boolean);

            return segments.length ? segments.join(' · ') : this.$t('tracking_timeline');
        },
        itemCountLabel() {
            return `${this.$t('count')}: ${this.items.length}`;
        },
    },
    methods: {
        closeDrawer() {
            if (this.$refs.timelineDrawer && typeof this.$refs.timelineDrawer.requestClose === 'function') {
                this.$refs.timelineDrawer.requestClose();
                return;
            }

            this.closeModal();
        },
        fetchTimeline() {
            this.preloader = true;

            this.axiosGet(`${GET_TRACKING}/${this.timelineData.trip_id}`)
                .then((response) => {
                    this.items = Array.isArray(response.data) ? response.data : [];
                })
                .catch(({ response }) => {
                    this.$toastr.e(response?.data?.message || this.$t('something_went_wrong'));
                })
                .finally(() => {
                    this.preloader = false;
                });
        },
    },
    mounted() {
        this.fetchTimeline();
    }
}
</script>

<style>
.trip-timeline-drawer__drawer-panel {
    min-width: 28rem;
    background: #ffffff;
}

.trip-timeline-drawer__drawer-header {
    padding: 0;
    border-bottom: 0;
    width: 100%;
}

.trip-timeline-drawer__drawer-body {
    padding: 0;
}

.trip-timeline-drawer__header {
    width: 100%;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 1rem;
    padding: 1.15rem 1.15rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    background: #ffffff;
}

.trip-timeline-drawer__header-copy {
    min-width: 0;
}

.trip-timeline-drawer__eyebrow {
    display: inline-block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.trip-timeline-drawer__header-copy h5 {
    margin: 0.35rem 0 0;
    color: var(--default-font-color, #0f172a);
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.trip-timeline-drawer__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.8rem;
}

.trip-timeline-drawer__meta-pill {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-height: 2rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--default-font-color, #0f172a);
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    white-space: normal;
    word-break: break-word;
}

.trip-timeline-drawer__meta-pill--accent {
    color: #0f766e;
    border-color: rgba(15, 118, 110, 0.16);
    background: rgba(15, 118, 110, 0.06);
}

.trip-timeline-drawer__close {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
    color: var(--default-font-color, #0f172a);
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    box-shadow: none;
    opacity: 1;
}

.trip-timeline-drawer__close:hover {
    background: #fff;
}

.trip-timeline-drawer__close:focus {
    outline: none;
}

.trip-timeline-drawer__header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.trip-timeline-drawer__refresh {
    width: 2.5rem;
    height: 2.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: var(--brand-color, #ff7a18);
    border: 1px solid rgba(255, 122, 24, 0.22);
    border-radius: 999px;
    background: rgba(255, 122, 24, 0.08);
}

.trip-timeline-drawer__refresh:disabled {
    cursor: wait;
    opacity: 0.55;
}

.trip-timeline-drawer__body-shell {
    min-height: 100%;
    padding: 1rem 1rem 1.25rem;
    background-color: #f8fafc;
}

.trip-timeline-drawer__summary {
    padding: 0.85rem 0.95rem;
    margin-bottom: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.9rem;
    background: #ffffff;
}

.trip-timeline-drawer__summary-kicker {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
}

.trip-timeline-drawer__summary-text {
    margin: 0.45rem 0 0;
    color: #475569;
    line-height: 1.55;
}

.trip-timeline-drawer .timeline {
    padding: 0.15rem 0 0.35rem;
}

.trip-timeline-drawer .timeline::before {
    left: 1.05rem;
    margin-left: 0;
    width: 2px;
    background-color: #e2e8f0;
}

.trip-timeline-drawer .timeline > li {
    margin-bottom: 0.9rem;
}

.trip-timeline-drawer .timeline > li > .timeline-panel {
    margin-left: 3.15rem;
    padding: 0.95rem 1rem 0.9rem;
    border-radius: 0.95rem;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    box-shadow: none;
}

.trip-timeline-drawer .timeline > li > .timeline-panel:hover {
    border-color: #cbd5e1;
    background: #fcfdff;
}

.trip-timeline-drawer .timeline > li .timeline-badge {
    left: 1.05rem;
    top: 0.95rem;
    width: 1.9rem;
    height: 1.9rem;
    margin-left: -0.95rem;
    font-size: 0.82rem;
    line-height: 1.9rem;
    box-shadow: 0 0 0 5px #f8fafc;
}

.trip-timeline-drawer .timeline > li .timeline-badge i {
    margin-top: 0;
    line-height: inherit;
}

.trip-timeline-drawer .timeline > li .timeline-badge + .timeline-panel::before {
    display: none;
}

.trip-timeline-drawer .timeline > li .timeline-badge + .timeline-panel::after {
    display: none;
}

.trip-timeline-drawer .timeline-title {
    margin-right: 0;
    font-size: 1rem;
    line-height: 1.35;
}

.trip-timeline-drawer .timeline-body {
    color: #475569;
    line-height: 1.55;
}

.trip-timeline-drawer .timeline-body p {
    margin-bottom: 0;
}

.trip-timeline-drawer .timeline-panel-controls {
    position: static !important;
}

.trip-timeline-drawer .timestamp {
    color: #64748b;
    font-size: 0.76rem;
    font-weight: 600;
}

.trip-timeline-drawer .trip-timeline-entry__head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: start;
    margin-bottom: 0.8rem;
}

.trip-timeline-drawer .trip-timeline-entry__title-block {
    min-width: 0;
}

.trip-timeline-drawer .trip-timeline-entry__type {
    display: inline-block;
    margin-bottom: 0.22rem;
    font-size: 0.69rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
}

.trip-timeline-drawer .trip-timeline-entry__title {
    color: #0f172a;
    overflow-wrap: anywhere;
}

.trip-timeline-drawer .trip-timeline-entry__controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.trip-timeline-drawer .trip-timeline-entry__timestamp {
    display: inline-flex;
    align-items: center;
    min-height: 1.9rem;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.trip-timeline-drawer .trip-timeline-entry__body {
    display: grid;
    gap: 0.75rem;
}

.trip-timeline-drawer .trip-timeline-entry__fact-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.7rem;
}

.trip-timeline-drawer .trip-timeline-entry__fact {
    display: grid;
    gap: 0.28rem;
    min-width: 0;
}

.trip-timeline-drawer .trip-timeline-entry__fact--person {
    align-content: start;
}

.trip-timeline-drawer .trip-timeline-entry__fact-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
}

.trip-timeline-drawer .trip-timeline-entry__fact-value {
    color: #0f172a;
    font-size: 0.92rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
}

.trip-timeline-drawer .trip-timeline-entry__person {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
}

.trip-timeline-drawer .trip-timeline-entry__avatar {
    width: 2.35rem;
    height: 2.35rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    overflow: hidden;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #0f172a;
    background: #e2e8f0;
    border: 1px solid #dbe4f0;
}

.trip-timeline-drawer .trip-timeline-entry__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.trip-timeline-drawer .trip-timeline-entry__avatar--escorter {
    background: rgba(14, 165, 233, 0.1);
    color: #0369a1;
    border-color: rgba(14, 165, 233, 0.16);
}

.trip-timeline-drawer .trip-timeline-entry__avatar--user {
    background: rgba(15, 118, 110, 0.08);
    color: #0f766e;
    border-color: rgba(15, 118, 110, 0.16);
}

.trip-timeline-drawer .trip-timeline-entry__status-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 1.9rem;
    width: fit-content;
    max-width: 100%;
    padding: 0.28rem 0.7rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #334155;
}

.trip-timeline-drawer .trip-timeline-entry__status-chip--success {
    color: #0f766e;
    background: rgba(15, 118, 110, 0.08);
    border-color: rgba(15, 118, 110, 0.16);
}

.trip-timeline-drawer .trip-timeline-entry__status-chip--warning {
    color: #b45309;
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.18);
}

.trip-timeline-drawer .trip-timeline-entry__status-chip--danger {
    color: #b91c1c;
    background: rgba(239, 68, 68, 0.08);
    border-color: rgba(239, 68, 68, 0.16);
}

.trip-timeline-drawer .trip-timeline-entry__status-chip--info {
    color: #0369a1;
    background: rgba(14, 165, 233, 0.08);
    border-color: rgba(14, 165, 233, 0.16);
}

.trip-timeline-drawer .trip-timeline-entry__contact-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.trip-timeline-drawer .trip-timeline-entry__contact-chip {
    display: inline-flex;
    align-items: center;
    min-height: 1.9rem;
    padding: 0.28rem 0.68rem;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 0.8rem;
    font-weight: 600;
    text-decoration: none;
}

.trip-timeline-drawer .trip-timeline-entry__contact-chip:hover {
    color: #0f766e;
    text-decoration: none;
}

.trip-timeline-drawer .trip-timeline-entry__comment {
    padding: 0.75rem 0.8rem;
    border-radius: 0.8rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #334155;
    line-height: 1.6;
    white-space: pre-line;
}

.trip-timeline-drawer .trip-timeline-entry__footer {
    display: block;
    padding-top: 0.7rem;
    border-top: 1px solid #f1f5f9;
    color: #64748b;
    font-size: 0.78rem;
}

.trip-timeline-drawer .trip-timeline-entry__author-wrap {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
}

.trip-timeline-drawer .trip-timeline-entry__author-copy {
    min-width: 0;
    display: grid;
    gap: 0.16rem;
}

.trip-timeline-drawer .trip-timeline-entry__author strong {
    color: #475569;
}

.trip-timeline-drawer .trip-timeline-entry__email {
    color: #0f766e;
    text-decoration: none;
    overflow-wrap: anywhere;
}

.trip-timeline-drawer .trip-timeline-entry__email:hover {
    text-decoration: underline;
}

.trip-timeline-drawer__empty {
    min-height: 16rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    padding: 2rem 1rem;
    text-align: center;
    border: 1px dashed rgba(148, 163, 184, 0.3);
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.85);
    color: var(--secondary, #64748b);
}

.trip-timeline-drawer__empty-icon {
    width: 3rem;
    height: 3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    color: #0f766e;
    background: rgba(15, 118, 110, 0.1);
}

@media (max-width: 991.98px) {
    .trip-timeline-drawer__drawer-panel {
        width: 100vw !important;
        min-width: 0;
    }

    .trip-timeline-drawer__header {
        grid-template-columns: 1fr auto;
    }

    .trip-timeline-drawer .trip-timeline-entry__fact-grid {
        grid-template-columns: 1fr;
    }

    .trip-timeline-drawer .trip-timeline-entry__head {
        grid-template-columns: 1fr;
    }
}
</style>
